import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FormControlDefinition } from 'src/app/model/FormControlDefinition';
import { UserService } from 'src/app/services/user/user.service';

/** FileWrapper interface is used to hold important information about a file for the class */
interface FileWrapper {
  name: string;      // Name of the file
  content: File;     // File content
  uploaded: boolean; // Whether the file has been uploaded
}



/**
 * Form component renders forms for user input using generic form control definitions
 * 
 * @note Some of the definitions here are a bit hacky and are listed below
 * - For matching passwords and displaying errors, you should use a custom validating function
 *   by subscribing to value changes and updating the form group control with the
 *   error 'noPasswordMatch'
 * 
 * @note I think the user can upload multiple files over and over if they change what they submit.
 *   As of now it's easy to leave that as is but might need to revisit that in the future, i hope i cant merge
 */
@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    styleUrl: './form.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormComponent {

  @Input() formDefinition!: FormControlDefinition[];

  /** Form group to represent form input fields */
  @Input() formGroup!: FormGroup;

  @Output() enterPressed = new EventEmitter<void>();

  /** Files for associated fields. Each file is mapped to the name of the input field */
  public files = new Map<string, FileWrapper>();

  /**
   * Default constructor for form component to autowire necessary fields
   */
  constructor(
    private _http: HttpClient,
    private _userService: UserService,
  ) { }

  /**
   * Handle files for file inputs being uploaded
   * 
   * @param input Template reference variable of the file input
   * @param field FormGroup key of the file input. Should point to a FormControl
   * of type <File | null>
   */
  onFileChange(input: HTMLInputElement, field: string) {
    const formControl: FormControl<File | null> =
      this.formGroup.controls[field] as FormControl<File | null>;
      if (input.files === null) {
        throw new Error('Input is not of type "file"')
      }
    if (input.files.length === 0) {
      this.formGroup.controls[field].setValue(null);
      this.files.delete(field);
    } else {
      if (input.files.length > 1) {
        throw new Error('Only one file is allowed to be uploaded')
      }

      // Add the file to the map
      this.files.set(field, {
        name: input.files[0].name,
        content: input.files[0],
        uploaded: false
      });

      formControl.setValue(input.files[0]);
    }
  }

  /**
   * Remove data from an input field
   * 
   * @param input Template reference variable of the file input
   * @param field FormGroup key of the file input. Should point to a FormControl
   * of type <File | null>
   */
  removeFile(input: HTMLInputElement, field: string) {
    input.value = '';
    this.onFileChange(input, field);
  }

  doLogin(field: FormControlDefinition) {
    if (field.type === 'password') {
      this.enterPressed.emit();
    }
  }
}
